import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import '../Style/App.css';
import { US_STATES, COUNTRIES, FLORIDA_LAW_ENFORCEMENT_AGENCIES, FLORIDA_COUNTIES } from '../constants/states';
import { useRole } from '../context/RoleContext';
import { useAlert } from '../context/AlertContext';
import CountyDropdown from './CountyDropdown';
import AgencyDropdown from './AgencyDropdown';
import { validatePhoneForSubmission, validateEmailForSubmission } from '../utils/phoneFormatter';

// Custom styles for editable fields in the modal
const editableFieldStyle = {
  marginTop: '5px',
  padding: '6px 10px',
  fontSize: '14px'
};

// Custom styles for view-only fields (greyed out)
const viewOnlyFieldStyle = {
  marginTop: '5px',
  padding: '6px 10px',
  fontSize: '14px',
  backgroundColor: '#f8f9fa',
  color: '#6c757d',
  border: '1px solid #e9ecef',
  borderRadius: '4px',
  cursor: 'not-allowed'
};

// Helper function to format phone numbers for display
const formatPhoneForDisplay = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // Format as XXX-XXX-XXXX if we have 10 digits
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Return original if not 10 digits
  return phone;
};

// Field mappings for consistent naming between camelCase and snake_case
const getFieldMappings = () => ({
  'contactDate': 'contact_date',
  'ori': 'ori',
  'agencyName': 'agency_name',
  'is_submitter_dio': 'is_submitter_dio',
  'is_non_criminal_arrest': 'is_non_criminal_arrest',
  'officerFirstName': 'officer_first_name',
  'officerLastName': 'officer_last_name',
  'badgeOrId': 'badge_or_id',
  'officerTitle': 'officer_title',
  'officerPhone': 'officer_phone',
  'officerEmail': 'officer_email',
  'contactCounty': 'contact_county',
  'contactStreetAddress': 'contact_street_address',

  'contactCity': 'contact_city',
  'firstName': 'first_name',
  'middleName': 'middle_name',
  'lastName': 'last_name',
  'countryOfCitizenship': 'country_of_citizenship',
  'hasDualCitizenship': 'has_dual_citizenship',
  'dualCitizenshipCountry': 'dual_citizenship_country',
  'alienRegistrationNumber': 'alien_registration_number',
  'dateOfBirth': 'date_of_birth',
  'sex': 'sex',
  'currentStreetAddress': 'current_street_address',
  'currentCity': 'current_city',
  'currentState': 'current_state',
  'currentZipCode': 'current_zip_code',
  'phoneNumber': 'phone_number',
  'employerName': 'employer_name',
  'employerStreetAddress': 'employer_street_address',
  'hasGangAffiliations': 'has_gang_affiliations',
  'gangName': 'gang_name',
  'contactDescription': 'contact_description',
  'federalAgencyContact': 'federal_agency_contact',
  'collaborationAgency': 'collaboration_agency',
  'hasCriminalHistory': 'has_criminal_history',
  'wasArrested': 'was_arrested',
  'used287gAuthority': 'used_287g_authority',
  'vehicleYear': 'vehicle_year',
  'vehicleMake': 'vehicle_make',
  'vehicleModel': 'vehicle_model',
  'vehicleTag': 'vehicle_tag',
  'vehicleTagState': 'vehicle_tag_state',
  'citizenshipVerifiedWithIce': 'citizenship_verified_with_ice',
  'iceCallCenterContact': 'ice_call_center_contact',
  'arrestCharges': 'arrest_charges',
  'iceContacted': 'ice_contacted',
  'iceResponded': 'ice_responded',
  'alienReleasedByIce': 'alien_released_by_ice',
  'notDetainedReason': 'not_detained_reason',
  'iceNoResponseReason': 'ice_no_response_reason',
  'wasAlienTransportedOnBehalfOfIce': 'was_alien_transported_on_behalf_of_ice',
  'photoUrl': 'photo_url',
  // TFM Arrest Sheet Fields
  'tfmLawEnforcementAgency': 'tfm_law_enforcement_agency',
  'tfmTaskForceOfficerName': 'tfm_task_force_officer_name',
  'tfmBadgeNumber': 'tfm_badge_number',
  'tfmIceCredentialNumber': 'tfm_ice_credential_number',
  'tfmCellPhone': 'tfm_cell_phone',
  'tfmArrestDate': 'tfm_arrest_date',
  'tfmArrestTime': 'tfm_arrest_time',
  'tfmEventNumber': 'tfm_event_number',
  'tfmIceApprovalOfficerName': 'tfm_ice_approval_officer_name',
  'tfmIceApprovalDatetime': 'tfm_ice_approval_datetime',
  'tfmArrestLocation': 'tfm_arrest_location',
  'tfmConsentGrantedBy': 'tfm_consent_granted_by',
  'tfmConsentTime': 'tfm_consent_time',
  'tfmSearchCompletedTime': 'tfm_search_completed_time',
  'tfmInvestigationHours': 'tfm_investigation_hours',
  'tfmNotes': 'tfm_notes',
  // TFM Alien Information
  'tfmAlienName': 'tfm_alien_name',
  'tfmAlienDob': 'tfm_alien_dob',
  'tfmAlienANumber': 'tfm_alien_a_number',
  'tfmAlienSex': 'tfm_alien_sex',
  'tfmAlienNationality': 'tfm_alien_nationality',
  'tfmAlienFbiNumber': 'tfm_alien_fbi_number'
});

// Create a separate component for the modal content
const ModalContent = ({ record, onClose, onApprove, onUpdate, startInEditMode = false, viewContext = null }) => {
  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // All state declarations at the top
  const [isEditing, setIsEditing] = useState(startInEditMode);
  const [editableRecord, setEditableRecord] = useState({});
  const [approving, setApproving] = useState(false);
  const [showApprovalSuccess, setShowApprovalSuccess] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [selectedPhotoFile, setSelectedPhotoFile] = useState(null);

  // Ref to store timeout ID for cleanup
  const approvalTimeoutRef = useRef(null);

  // Get user role and permissions
  const { canApproveRecords, canEditRecords, userRole, isAdmin, isSecurityAdmin, isOfficer } = useRole();
  const { showError, showSuccess } = useAlert();

  // Initialize editable record when record changes
  useEffect(() => {
    if (record) {
      setEditableRecord(record);

      // Clear any existing approval timeout when loading new record
      if (approvalTimeoutRef.current) {
        clearTimeout(approvalTimeoutRef.current);
        approvalTimeoutRef.current = null;
      }

      // Reset approval states
      setApproving(false);
      setShowApprovalSuccess(false);

      // If starting in edit mode, initialize the editable record
      if (startInEditMode) {
        console.log('Starting in edit mode for record:', record);
        const recordCopy = JSON.parse(JSON.stringify(record));
        setEditableRecord(recordCopy);
      }
    }
  }, [record, startInEditMode]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (approvalTimeoutRef.current) {
        clearTimeout(approvalTimeoutRef.current);
        approvalTimeoutRef.current = null;
      }
    };
  }, []);

  // Clear any stale timeouts when modal opens
  useEffect(() => {
    if (approvalTimeoutRef.current) {
      clearTimeout(approvalTimeoutRef.current);
      approvalTimeoutRef.current = null;
    }
    setApproving(false);
    setShowApprovalSuccess(false);
  }, []); // Run once when component mounts

  // Prevent automatic scrolling when date inputs are focused
  const handleDateFocus = (e) => {
    // Store the current scroll position of all scrollable containers
    const scrollContainer = e.target.closest('.modalBody');
    const bodyScrollTop = document.body.scrollTop || document.documentElement.scrollTop;

    if (scrollContainer) {
      const currentScrollTop = scrollContainer.scrollTop;

      // Use multiple requestAnimationFrame calls to ensure this runs after all browser behaviors
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollContainer.scrollTop = currentScrollTop;
          document.body.scrollTop = bodyScrollTop;
          document.documentElement.scrollTop = bodyScrollTop;
        });
      });
    }
  };

  // Simple date input handler - let HTML5 min/max handle year validation
  const handleDateInputChange = (e) => {
    // Just pass through to the original handler
    // HTML5 min/max attributes will handle year validation
    handleInputChange(e);
  };

  // Handle click events on date inputs to prevent scrolling
  const handleDateClick = (e) => {
    const scrollContainer = e.target.closest('.modalBody');
    const bodyScrollTop = document.body.scrollTop || document.documentElement.scrollTop;

    if (scrollContainer) {
      const currentScrollTop = scrollContainer.scrollTop;

      // Prevent scrolling when date picker opens
      setTimeout(() => {
        scrollContainer.scrollTop = currentScrollTop;
        document.body.scrollTop = bodyScrollTop;
        document.documentElement.scrollTop = bodyScrollTop;
      }, 0);

      // Additional check after a longer delay for slower browsers
      setTimeout(() => {
        scrollContainer.scrollTop = currentScrollTop;
        document.body.scrollTop = bodyScrollTop;
        document.documentElement.scrollTop = bodyScrollTop;
      }, 100);
    }
  };

  // Check if record is approved (should be read-only)
  const isApprovedRecord = record && record.is_approved;

  // Handle edit button click
  const handleEditClick = () => {
    // Don't allow editing of approved records unless user is Admin or Security Admin
    if (isApprovedRecord && !isAdmin && !isSecurityAdmin) {
      showError('Approved records cannot be edited.');
      return;
    }

    // Initialize editable record with current record data
    // Create a deep copy to avoid reference issues
    const recordCopy = JSON.parse(JSON.stringify(record));

    // Log the record being edited for debugging
    console.log('Editing record:', recordCopy);

    setEditableRecord(recordCopy);
    setIsEditing(true);
  };

  // Convert PNG to JPEG to avoid connection issues
  const convertPngToJpeg = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        // Fill with white background (for transparency)
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the image
        ctx.drawImage(img, 0, 0);

        // Convert to JPEG blob
        canvas.toBlob((blob) => {
          if (blob) {
            // Create a new File object with JPEG type
            const jpegFile = new File([blob], file.name.replace(/\.png$/i, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(jpegFile);
          } else {
            reject(new Error('Failed to convert PNG to JPEG'));
          }
        }, 'image/jpeg', 0.9); // 90% quality
      };

      img.onerror = () => reject(new Error('Failed to load image for conversion'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Recompress JPEG to reduce file size and remove problematic metadata
  const recompressJpeg = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Optionally resize if very large
        let { width, height } = img;
        const maxDimension = 2048; // Max width or height

        if (width > maxDimension || height > maxDimension) {
          const ratio = Math.min(maxDimension / width, maxDimension / height);
          width = Math.floor(width * ratio);
          height = Math.floor(height * ratio);
          console.log(`Resizing image from ${img.width}x${img.height} to ${width}x${height}`);
        }

        canvas.width = width;
        canvas.height = height;

        // Draw the image (this removes metadata and recompresses)
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to JPEG blob with lower quality for smaller size
        canvas.toBlob((blob) => {
          if (blob) {
            const jpegFile = new File([blob], file.name, {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            console.log(`JPEG recompressed: ${(file.size / 1024 / 1024).toFixed(2)}MB → ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
            resolve(jpegFile);
          } else {
            reject(new Error('Failed to recompress JPEG'));
          }
        }, 'image/jpeg', 0.8); // 80% quality for better compression
      };

      img.onerror = () => reject(new Error('Failed to load image for recompression'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Convert any image format to JPEG
  const convertToJpeg = (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;

        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // Draw the image
        ctx.drawImage(img, 0, 0);

        // Convert to JPEG blob
        canvas.toBlob((blob) => {
          if (blob) {
            const jpegFile = new File([blob], file.name.replace(/\.[^.]+$/i, '.jpg'), {
              type: 'image/jpeg',
              lastModified: Date.now()
            });
            resolve(jpegFile);
          } else {
            reject(new Error('Failed to convert to JPEG'));
          }
        }, 'image/jpeg', 0.9);
      };

      img.onerror = () => reject(new Error('Failed to load image for conversion'));
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle file upload for photos
  const handlePhotoUpload = async (file) => {
    console.log('🚀 handlePhotoUpload called with file:', file);

    if (!file) {
      console.log(' No file provided to handlePhotoUpload');
      return null;
    }

    setUploadingPhoto(true);

    try {

      // Check file size limit (5MB)
      if (file.size > 5 * 1024 * 1024) {
        showError('File is too large. Please choose an image smaller than 5MB.');
        return null;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        showError('Please select an image file (JPG, PNG, GIF, etc.).');
        return null;
      }

      console.log('File validation passed, starting upload...');

      // For files that might be problematic, try to recompress them
      let fileToUpload = file;
      if (file.size > 1000000) { // Files over 1MB
        console.log(` Large ${file.type} detected (${(file.size / 1024 / 1024).toFixed(2)}MB), attempting to recompress...`);
        try {
          if (file.type === 'image/png') {
            fileToUpload = await convertPngToJpeg(file);
            console.log(' PNG converted to JPEG successfully');
          } else if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
            fileToUpload = await recompressJpeg(file);
            console.log(' JPEG recompressed successfully');
          } else {
            fileToUpload = await convertToJpeg(file);
            console.log(' Image converted to JPEG successfully');
          }
        } catch (conversionError) {
          console.log(' Image processing failed, using original file:', conversionError);
          fileToUpload = file;
        }
      }
      const uploadUrl = process.env.NODE_ENV === 'production' ? '/api/upload-image' : 'http://localhost:8080/api/upload-image';
      console.log('Upload URL:', uploadUrl);

      // Create a smaller test first for problematic files
      if (file.size > 1024 * 1024) { // If file is larger than 1MB
        console.log(' Large file detected, this might cause connection issues');
      }

      const formData = new FormData();
      formData.append('photo', fileToUpload);
      console.log('FormData created successfully with file:', fileToUpload.name, fileToUpload.type, fileToUpload.size);

      const token = localStorage.getItem('idToken');
      console.log('Token available:', !!token);
      console.log('About to make axios request...');

      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: token ? `Bearer ${token}` : ''
        },
        timeout: 30000, // Reduce timeout to 30 seconds to fail faster
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('Modal upload progress:', percentCompleted + '%');
        }
      });

      return response.data.imagePath;
    } catch (error) {
      console.error('=== MODAL IMAGE UPLOAD ERROR ===');
      console.error('Error details:', {
        message: error.message,
        code: error.code,
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      console.error('Full error object:', error);

      // Provide more specific error messages
      let errorMessage = 'Failed to upload image. ';
      if (error.code === 'ERR_NETWORK') {
        errorMessage += `Network connection error. This might be due to file size (${(file.size / 1024 / 1024).toFixed(2)}MB) or connection issues.`;
      } else if (error.code === 'ECONNABORTED') {
        errorMessage += 'Upload timeout. The file may be too large or the connection is slow.';
      } else if (error.response?.status === 413) {
        errorMessage += 'File is too large. Please choose a smaller image.';
      } else if (error.response?.status === 400) {
        errorMessage += 'Invalid file type. Please upload an image file.';
      } else if (error.response?.status === 500) {
        errorMessage += 'Server error. Please try again or contact support.';
      } else {
        errorMessage += `Please try again. (Error: ${error.code || 'Unknown'})`;
      }

      showError(errorMessage);
      return null;
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle file selection for photo upload
  const handlePhotoFileChange = async (e) => {
    console.log('=== FILE SELECTION DEBUG ===');
    console.log('File input event triggered');

    const file = e.target.files[0];
    if (!file) {
      console.log('No file selected');
      return;
    }

    console.log('File selected:', {
      name: file.name,
      size: file.size,
      type: file.type,
      lastModified: new Date(file.lastModified)
    });

    setSelectedPhotoFile(file);

    try {
      // Automatically upload the file and update the record
      console.log('About to call handlePhotoUpload...');
      const uploadedImagePath = await handlePhotoUpload(file);
      console.log('handlePhotoUpload returned:', uploadedImagePath);

      if (uploadedImagePath) {
        // Update the editable record with the new photo path
        const updatedRecord = {
          ...editableRecord,
          photo_url: uploadedImagePath,
          photoUrl: uploadedImagePath
        };
        setEditableRecord(updatedRecord);

        // Clear the selected file since it's been uploaded
        setSelectedPhotoFile(null);

        // Reset the file input
        e.target.value = '';

        showSuccess('Image uploaded successfully! Remember to save your changes.');
      } else {
        // Upload failed - clear the file input and don't update the record
        setSelectedPhotoFile(null);
        e.target.value = '';
        console.log('Image upload failed, record not updated');
      }
    } catch (error) {
      console.error('=== FILE CHANGE HANDLER ERROR ===');
      console.error('Error in handlePhotoFileChange:', error);
      setSelectedPhotoFile(null);
      e.target.value = '';
      showError('Error processing file: ' + error.message);
    }
  };

  // Handle input changes in edit mode
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Debug logging for dual citizenship fields
    if (name === 'hasDualCitizenship' || name === 'dualCitizenshipCountry') {
      console.log(`Modal - Dual citizenship field changed: ${name} = ${type === 'checkbox' ? checked : value}`);
    }

    // Fields that should have title case formatting
    const titleCaseFields = [
      'officerFirstName', 'officerLastName', 'badgeOrId', 'officerTitle',
      'contactCity', 'contactStreetAddress', 'firstName', 'middleName', 'lastName',
      'currentStreetAddress', 'currentCity', 'vehicleMake', 'vehicleModel', 'vehicleTag',
      'employerName', 'employerStreetAddress', 'gangName', 'iceCallCenterContact',
      'notDetainedReason', 'iceNoResponseReason',
      // TFM fields that need title case
      'tfmLawEnforcementAgency', 'tfmTaskForceOfficerName', 'tfmIceApprovalOfficerName',
      'tfmArrestLocation', 'tfmAlienName', 'tfmAlienNationality', 'tfmConsentGrantedBy',
      // Snake_case versions
      'officer_first_name', 'officer_last_name', 'badge_or_id', 'officer_title',
      'contact_city', 'contact_street_address', 'first_name', 'middle_name', 'last_name',
      'current_street_address', 'current_city', 'vehicle_make', 'vehicle_model', 'vehicle_tag',
      'employer_name', 'employer_street_address', 'gang_name', 'ice_call_center_contact',
      'not_detained_reason', 'ice_no_response_reason',
      // TFM snake_case versions
      'tfm_law_enforcement_agency', 'tfm_task_force_officer_name', 'tfm_ice_approval_officer_name',
      'tfm_arrest_location', 'tfm_alien_name', 'tfm_alien_nationality', 'tfm_consent_granted_by'
    ];

    // Get field mappings for consistent naming
    const fieldMappings = getFieldMappings();

    // For date fields, ensure we store the value in a consistent format
    let processedValue = type === 'checkbox' ? checked : value;

    // Apply title case formatting for specific text fields
    if (type === 'text' && titleCaseFields.includes(name) && typeof value === 'string') {
      processedValue = toTitleCase(value);
    }

    // If it's a date input, keep it in YYYY-MM-DD format to avoid timezone issues
    if (type === 'date' && value) {
      // Keep the date in YYYY-MM-DD format as received from the input
      processedValue = value;
    }

    // Get the corresponding snake_case field name
    const snakeKey = fieldMappings[name] || name.replace(/([A-Z])/g, '_$1').toLowerCase();

    // Create the updated record with the new value in both formats
    let updatedRecord = {
      ...editableRecord,
      [name]: processedValue,
      [snakeKey]: processedValue
    };

    // Show/hide conditional fields - clear dependent fields when parent checkbox is unchecked
    if (name === 'hasDualCitizenship' && !checked) {
      updatedRecord = { ...updatedRecord, dualCitizenshipCountry: '', dual_citizenship_country: '' };
    }
    if (name === 'hasGangAffiliations' && !checked) {
      updatedRecord = { ...updatedRecord, gangName: '', gang_name: '' };
    }
    if (name === 'wasArrested' && !checked) {
      updatedRecord = { ...updatedRecord, arrestCharges: '', arrest_charges: '' };
    }
    // COMMENTED OUT: Don't auto-clear ICE fields when arrested - ICE is contacted for arrests too
    // if (name === 'wasArrested' && checked) {
    //   // When arrested, clear ICE contact fields
    //   updatedRecord = {
    //     ...updatedRecord,
    //     iceContacted: false,
    //     ice_contacted: false,
    //     iceResponded: false,
    //     ice_responded: false,
    //     alienReleasedByIce: '',
    //     alien_released_by_ice: '',
    //     notDetainedReason: '',
    //     not_detained_reason: '',
    //     iceNoResponseReason: '',
    //     ice_no_response_reason: ''
    //   };
    // }
    if (name === 'citizenshipVerifiedWithIce' && processedValue !== 'Yes') {
      updatedRecord = {
        ...updatedRecord,
        iceCallCenterContact: '',
        ice_call_center_contact: ''
      };
    }
    if (name === 'iceContacted' && (processedValue === 'No' || processedValue === '')) {
      updatedRecord = {
        ...updatedRecord,
        iceResponded: '',
        ice_responded: '',
        alienReleasedByIce: '',
        alien_released_by_ice: '',
        notDetainedReason: '',
        not_detained_reason: '',
        iceNoResponseReason: '',
        ice_no_response_reason: ''
      };
    }
    if (name === 'iceResponded' && (processedValue === 'No' || processedValue === 'ICE was not contacted' || processedValue === '')) {
      updatedRecord = {
        ...updatedRecord,
        alienReleasedByIce: '',
        alien_released_by_ice: '',
        notDetainedReason: '',
        not_detained_reason: '',
        iceNoResponseReason: '',
        ice_no_response_reason: ''
      };
    }
    if (name === 'alienReleasedByIce' && processedValue !== 'No') {
      updatedRecord = {
        ...updatedRecord,
        notDetainedReason: '',
        not_detained_reason: ''
      };
    }

    // Update the editable record
    setEditableRecord(updatedRecord);

    console.log('Updated editableRecord:', updatedRecord);



    // Debug logging for dual citizenship fields
    if (name === 'hasDualCitizenship' || name === 'dualCitizenshipCountry') {
      console.log('Modal - Updated dual citizenship data:', {
        hasDualCitizenship: updatedRecord.hasDualCitizenship,
        has_dual_citizenship: updatedRecord.has_dual_citizenship,
        dualCitizenshipCountry: updatedRecord.dualCitizenshipCountry,
        dual_citizenship_country: updatedRecord.dual_citizenship_country
      });
    }
  };

  // Add saving state
  const [saving, setSaving] = useState(false);

  // Handle save changes
  const handleSaveChanges = async () => {
    try {
      // Set loading state
      setSaving(true);

      // Get field mappings for proper data conversion
      const fieldMappings = getFieldMappings();

      // Start with the original record to preserve all existing data
      const updatedRecord = { ...record };

      // Apply changes from editableRecord, handling both camelCase and snake_case
      Object.keys(editableRecord).forEach(key => {
        const value = editableRecord[key];

        // Set the value using the original key
        updatedRecord[key] = value;

        // If this is a camelCase key, also set the corresponding snake_case key
        if (fieldMappings[key]) {
          updatedRecord[fieldMappings[key]] = value;
        }

        // If this is a snake_case key, also set the corresponding camelCase key
        const reverseMappings = Object.fromEntries(
          Object.entries(fieldMappings).map(([camel, snake]) => [snake, camel])
        );
        if (reverseMappings[key]) {
          updatedRecord[reverseMappings[key]] = value;
        }
      });

      // Validate phone numbers using centralized validation
      const phoneError = validatePhoneForSubmission(editableRecord.phoneNumber || editableRecord.phone_number, 'Phone Number');
      if (phoneError) {
        showError(phoneError);
        return;
      }

      const employerPhoneError = validatePhoneForSubmission(editableRecord.employerPhoneNumber || editableRecord.employer_phone_number, 'Employer Phone Number');
      if (employerPhoneError) {
        showError(employerPhoneError);
        return;
      }

      // Validate submitter email using centralized validation
      const emailError = validateEmailForSubmission(editableRecord.officerEmail || editableRecord.officer_email, 'Submitter Email Address');
      if (emailError) {
        showError(emailError);
        return;
      }

      // Ensure all required fields are present (check both camelCase and snake_case)
      const requiredFields = [
        'contactDate', 'ori', 'agencyName', 'officerFirstName', 'officerLastName', 'badgeOrId', 'officerTitle', 'officerPhone', 'officerEmail',
        'contactCounty', 'contactStreetAddress', 'contactCity',
        'firstName', 'lastName', 'countryOfCitizenship', 'dateOfBirth', 'sex'
      ];

      // Check for missing fields and try to populate from alternative naming
      const missingFields = [];
      requiredFields.forEach(field => {
        const snakeField = fieldMappings[field] || field.replace(/([A-Z])/g, '_$1').toLowerCase();

        // Check if the field exists in either format
        if ((!updatedRecord[field] && updatedRecord[field] !== 0) &&
            (!updatedRecord[snakeField] && updatedRecord[snakeField] !== 0)) {
          missingFields.push(field);
        } else {
          // Ensure both formats are populated
          if (updatedRecord[field] && !updatedRecord[snakeField]) {
            updatedRecord[snakeField] = updatedRecord[field];
          } else if (updatedRecord[snakeField] && !updatedRecord[field]) {
            updatedRecord[field] = updatedRecord[snakeField];
          }
        }
      });

      if (missingFields.length > 0) {
        const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
        console.error(errorMsg);
        showError(errorMsg);
        throw new Error(errorMsg);
      }

      // Call the onUpdate callback if provided
      if (onUpdate) {
        // Wait for the update to complete
        await onUpdate(updatedRecord);

        // Show success message
        showSuccess('Changes saved successfully!');
      } else {
        // If no onUpdate callback, just show a message
        console.warn('No onUpdate callback provided, changes will not be persisted');
        showSuccess('Changes saved! (Note: Changes are not being persisted to the backend)');
      }

      // Exit edit mode
      setIsEditing(false);
    } catch (error) {
      // Show error message
      console.error('Error saving changes:', error);
      showError('Failed to save changes: ' + error.message);
    } finally {
      // Clear loading state
      setSaving(false);
    }
  };

  // Handle cancel edit
  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // Handle approve record
  const handleApproveRecord = async () => {
    if (!record || !record.id) {
      showError('Cannot approve: Record ID is missing');
      return;
    }

    // Clear any existing timeout
    if (approvalTimeoutRef.current) {
      clearTimeout(approvalTimeoutRef.current);
      approvalTimeoutRef.current = null;
    }

    setApproving(true);
    setShowApprovalSuccess(false); // Reset success state

    try {
      // Call the onApprove function passed from parent component
      await onApprove(record.id);

      // Show success message and close modal immediately
      showSuccess(' Record approved successfully!');
      onClose();
    } catch (error) {
      console.error('Error approving record:', error);
      showError('Failed to approve record. Please try again.');
      setApproving(false);
    }
  };



  // Format date for input fields (yyyy-MM-dd) - fixed to handle timezone issues
  const formatDateForInput = (dateString) => {
    if (!dateString) return '';

    // If the date string is already in YYYY-MM-DD format, return it as-is
    if (typeof dateString === 'string' && dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateString;
    }

    // For other date formats, convert to YYYY-MM-DD
    const date = new Date(dateString);
    return date.toISOString().split('T')[0]; // Returns YYYY-MM-DD
  };



  // Helper function to get value from record (handles both camelCase and snake_case)
  const getValue = (key) => {
    // Use editableRecord when in edit mode, otherwise use record
    const sourceRecord = isEditing ? editableRecord : record;

    if (!sourceRecord) return 'N/A';

    // Get field mappings and create reverse mappings for snake_case to camelCase
    const fieldMappings = getFieldMappings();
    const keyMappings = Object.fromEntries(
      Object.entries(fieldMappings).map(([camel, snake]) => [snake, camel])
    );

    let value;

    // Try snake_case first (from API)
    if (sourceRecord[key] !== undefined) {
      value = sourceRecord[key];
    }
    // Try using the mapping
    else if (keyMappings[key] && sourceRecord[keyMappings[key]] !== undefined) {
      value = sourceRecord[keyMappings[key]];
    }
    // Try camelCase (from transformed records)
    else {
      const generatedCamelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      if (sourceRecord[generatedCamelKey] !== undefined) {
        value = sourceRecord[generatedCamelKey];
      }
      // Try converting camelCase to snake_case
      else {
        const snakeKey = key.replace(/([A-Z])/g, '_$1').toLowerCase();
        if (sourceRecord[snakeKey] !== undefined) {
          value = sourceRecord[snakeKey];
        }
      }
    }

    // Handle null/undefined values for form inputs
    if (value === null || value === undefined) {
      return '';
    }

    // For display purposes, convert empty strings to 'N/A'
    if (!isEditing && value === '') {
      return 'N/A';
    }

    return value;
  };

  return (
    <div className="modalContent">
      <div className="modalHeader">
        <div>
          <h2>Suspected Alien Record Details</h2>
          <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic' }}>
            Viewing as: {userRole || 'Loading...'}
          </span>
        </div>
        <button className="closeBtn" onClick={onClose}>&times;</button>
      </div>

      <div className="modalBody">
        {record ? (
          <div className="recordDetails">
            {/* Required FDLE Submission Data Title */}
            <h2 style={{
              color: 'var(--primary-color)',
              fontSize: '20px',
              marginBottom: '20px',
              textAlign: 'left',
              borderBottom: '2px solid var(--primary-color)',
              paddingBottom: '10px'
            }}>
              Required FDLE Submission Data
            </h2>

            <h3>1. Date of Contact</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Date of Contact:</span>
                {isEditing ? (
                  <input
                    type="date"
                    name="contactDate"
                    value={formatDateForInput(getValue('contact_date'))}
                    onChange={handleDateInputChange}
                    onFocus={handleDateFocus}
                    onClick={handleDateClick}
                    className="modern-input"
                    style={editableFieldStyle}
                    min="1000-01-01"
                    max="9999-12-31"
                  />
                ) : (
                  <input
                    type="date"
                    value={formatDateForInput(getValue('contact_date'))}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>

            <h3>2. Contacting Agency</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">ORI (Originating Agency Identifier):</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="ori"
                    value={getValue('ori')}
                    onChange={(e) => {
                      // Only allow digits, any length
                      const value = e.target.value.replace(/[^0-9]/g, '');
                      const syntheticEvent = {
                        target: {
                          name: 'ori',
                          value: value,
                          type: 'text'
                        }
                      };
                      handleInputChange(syntheticEvent);
                    }}
                    onKeyDown={(e) => {
                      // Prevent non-numeric characters from being typed
                      if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                        e.preventDefault();
                      }
                    }}
                    className="modern-input"
                    style={editableFieldStyle}
                    title="Agency ORI Number (numbers only)"
                    placeholder="Numbers only, no letters - Do not put FL"
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('ori')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('ori') ? "Numbers only, no letters - Do not put FL" : ""}
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Agency Name:</span>
                {isEditing ? (
                  <AgencyDropdown
                    name="agencyName"
                    value={getValue('agency_name')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <select
                    value={getValue('agency_name') || ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    {FLORIDA_LAW_ENFORCEMENT_AGENCIES.map((agency) => (
                      <option key={agency} value={agency}>
                        {agency}
                      </option>
                    ))}
                    <option value="CUSTOM">--- Enter Custom Agency ---</option>
                  </select>
                )}
              </div>
            </div>

            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Submitter's First Name:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="officerFirstName"
                    value={getValue('officer_first_name')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('officer_first_name')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Submitter's Last Name:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="officerLastName"
                    value={getValue('officer_last_name')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('officer_last_name')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Badge or ID#:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="badgeOrId"
                    value={getValue('badge_or_id')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('badge_or_id')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Submitter Title/Rank:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="officerTitle"
                    value={getValue('officer_title')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="e.g., Detective, Sergeant, Officer"
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('officer_title')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('officer_title') ? "e.g., Detective, Sergeant, Officer" : ""}
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Submitter Phone:</span>
                {isEditing ? (
                  <input
                    type="tel"
                    name="officerPhone"
                    value={formatPhoneForDisplay(getValue('officer_phone'))}
                    onChange={(e) => {
                      // Format phone number as XXX-XXX-XXXX
                      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                      // Limit to 10 digits
                      value = value.slice(0, 10);
                      // Format as XXX-XXX-XXXX
                      if (value.length >= 6) {
                        value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                      } else if (value.length >= 3) {
                        value = value.slice(0, 3) + '-' + value.slice(3);
                      }

                      const syntheticEvent = {
                        target: {
                          name: 'officerPhone',
                          value: value,
                          type: 'tel'
                        }
                      };
                      handleInputChange(syntheticEvent);
                    }}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="555-555-5555"
                    title="Enter valid 10-digit phone number XXX-XXX-XXXX"
                  />
                ) : (
                  <input
                    type="text"
                    value={formatPhoneForDisplay(getValue('officer_phone'))}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('officer_phone') ? "555-555-5555" : ""}
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Submitter Email:</span>
                {isEditing ? (
                  <input
                    type="email"
                    name="officerEmail"
                    value={getValue('officer_email')}
                    onChange={handleInputChange}
                    onBlur={(e) => {
                      // Validate email format on blur for immediate feedback
                      const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
                      const isValid = emailRegex.test(e.target.value.trim());
                      if (!isValid && e.target.value.trim() !== '') {
                        e.target.setCustomValidity('Please enter a valid email address (e.g., submitter@agency.gov)');
                      } else {
                        e.target.setCustomValidity('');
                      }
                    }}
                    className="modern-input"
                    style={{
                      ...editableFieldStyle,
                      borderColor: (() => {
                        const emailValue = getValue('officer_email');
                        if (!emailValue || emailValue.trim() === '') return '#ced4da';
                        const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
                        const isValid = emailRegex.test(emailValue.trim());
                        return isValid ? '#28a745' : '#dc3545'; // Green for valid, red for invalid
                      })()
                    }}
                    placeholder="submitter@agency.gov"
                    title="Enter a valid email address (e.g., submitter@agency.gov)"
                  />
                ) : (
                  <input
                    type="email"
                    value={getValue('officer_email')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('officer_email') ? "submitter@agency.gov" : ""}
                  />
                )}
              </div>
            </div>

            <h3>3-5. Location of Alien Contact</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">County of Alien Contact:</span>
                {isEditing ? (
                  <CountyDropdown
                    name="contactCounty"
                    value={getValue('contact_county')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <select
                    value={getValue('contact_county') || ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    {FLORIDA_COUNTIES.map((county) => (
                      <option key={county} value={county}>
                        {county}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">City of Alien Contact:</span>
                {isEditing ? (
                  <select
                    name="contactCity"
                    value={getValue('contact_city')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">Select City</option>
                    <option value="Alachua">Alachua</option>
                    <option value="Alford">Alford</option>
                    <option value="Altamonte Springs">Altamonte Springs</option>
                    <option value="Altha">Altha</option>
                    <option value="Anna Maria">Anna Maria</option>
                    <option value="Apalachicola">Apalachicola</option>
                    <option value="Apopka">Apopka</option>
                    <option value="Arcadia">Arcadia</option>
                    <option value="Archer">Archer</option>
                    <option value="Astatula">Astatula</option>
                    <option value="Atlantic Beach">Atlantic Beach</option>
                    <option value="Atlantis">Atlantis</option>
                    <option value="Auburndale">Auburndale</option>
                    <option value="Aventura">Aventura</option>
                    <option value="Avon Park">Avon Park</option>
                    <option value="Babson Park">Babson Park</option>
                    <option value="Bal Harbour">Bal Harbour</option>
                    <option value="Baldwin">Baldwin</option>
                    <option value="Bartow">Bartow</option>
                    <option value="Bascom">Bascom</option>
                    <option value="Bay Harbor Islands">Bay Harbor Islands</option>
                    <option value="Bay Lake">Bay Lake</option>
                    <option value="Bell">Bell</option>
                    <option value="Belle Glade">Belle Glade</option>
                    <option value="Belle Isle">Belle Isle</option>
                    <option value="Belleair">Belleair</option>
                    <option value="Belleair Beach">Belleair Beach</option>
                    <option value="Belleair Bluffs">Belleair Bluffs</option>
                    <option value="Belleair Shore">Belleair Shore</option>
                    <option value="Belleview">Belleview</option>
                    <option value="Beverly Beach">Beverly Beach</option>
                    <option value="Biscayne Park">Biscayne Park</option>
                    <option value="Blountstown">Blountstown</option>
                    <option value="Boca Raton">Boca Raton</option>
                    <option value="Bonifay">Bonifay</option>
                    <option value="Bonita Springs">Bonita Springs</option>
                    <option value="Bowling Green">Bowling Green</option>
                    <option value="Boynton Beach">Boynton Beach</option>
                    <option value="Bradenton">Bradenton</option>
                    <option value="Bradenton Beach">Bradenton Beach</option>
                    <option value="Branford">Branford</option>
                    <option value="Briny Breezes">Briny Breezes</option>
                    <option value="Bristol">Bristol</option>
                    <option value="Bronson">Bronson</option>
                    <option value="Brooker">Brooker</option>
                    <option value="Brooksville">Brooksville</option>
                    <option value="Bunnell">Bunnell</option>
                    <option value="Bushnell">Bushnell</option>
                    <option value="Callahan">Callahan</option>
                    <option value="Callaway">Callaway</option>
                    <option value="Campbellton">Campbellton</option>
                    <option value="Cape Canaveral">Cape Canaveral</option>
                    <option value="Cape Coral">Cape Coral</option>
                    <option value="Carrabelle">Carrabelle</option>
                    <option value="Caryville">Caryville</option>
                    <option value="Casselberry">Casselberry</option>
                    <option value="Cedar Key">Cedar Key</option>
                    <option value="Center Hill">Center Hill</option>
                    <option value="Century">Century</option>
                    <option value="Chattahoochee">Chattahoochee</option>
                    <option value="Chiefland">Chiefland</option>
                    <option value="Chipley">Chipley</option>
                    <option value="Cinco Bayou">Cinco Bayou</option>
                    <option value="Clearwater">Clearwater</option>
                    <option value="Clermont">Clermont</option>
                    <option value="Clewiston">Clewiston</option>
                    <option value="Cloud Lake">Cloud Lake</option>
                    <option value="Cocoa">Cocoa</option>
                    <option value="Cocoa Beach">Cocoa Beach</option>
                    <option value="Coconut Creek">Coconut Creek</option>
                    <option value="Coleman">Coleman</option>
                    <option value="Cooper City">Cooper City</option>
                    <option value="Coral Gables">Coral Gables</option>
                    <option value="Coral Springs">Coral Springs</option>
                    <option value="Cottondale">Cottondale</option>
                    <option value="Crescent City">Crescent City</option>
                    <option value="Crestview">Crestview</option>
                    <option value="Cross City">Cross City</option>
                    <option value="Crystal River">Crystal River</option>
                    <option value="Cutler Bay">Cutler Bay</option>
                    <option value="Dade City">Dade City</option>
                    <option value="Dania Beach">Dania Beach</option>
                    <option value="Davenport">Davenport</option>
                    <option value="Davie">Davie</option>
                    <option value="Daytona Beach">Daytona Beach</option>
                    <option value="Daytona Beach Shores">Daytona Beach Shores</option>
                    <option value="DeBary">DeBary</option>
                    <option value="Deerfield Beach">Deerfield Beach</option>
                    <option value="DeFuniak Springs">DeFuniak Springs</option>
                    <option value="DeLand">DeLand</option>
                    <option value="Delray Beach">Delray Beach</option>
                    <option value="Deltona">Deltona</option>
                    <option value="Destin">Destin</option>
                    <option value="Doral">Doral</option>
                    <option value="Dundee">Dundee</option>
                    <option value="Dunedin">Dunedin</option>
                    <option value="Dunnellon">Dunnellon</option>
                    <option value="Eagle Lake">Eagle Lake</option>
                    <option value="Eatonville">Eatonville</option>
                    <option value="Ebro">Ebro</option>
                    <option value="Edgewater">Edgewater</option>
                    <option value="Edgewood">Edgewood</option>
                    <option value="El Portal">El Portal</option>
                    <option value="Estero">Estero</option>
                    <option value="Esto">Esto</option>
                    <option value="Eustis">Eustis</option>
                    <option value="Everglades City">Everglades City</option>
                    <option value="Fanning Springs">Fanning Springs</option>
                    <option value="Fellsmere">Fellsmere</option>
                    <option value="Fernandina Beach">Fernandina Beach</option>
                    <option value="Flagler Beach">Flagler Beach</option>
                    <option value="Florida City">Florida City</option>
                    <option value="Fort Lauderdale">Fort Lauderdale</option>
                    <option value="Fort Meade">Fort Meade</option>
                    <option value="Fort Myers">Fort Myers</option>
                    <option value="Fort Myers Beach">Fort Myers Beach</option>
                    <option value="Fort Pierce">Fort Pierce</option>
                    <option value="Fort Walton Beach">Fort Walton Beach</option>
                    <option value="Fort White">Fort White</option>
                    <option value="Freeport">Freeport</option>
                    <option value="Frostproof">Frostproof</option>
                    <option value="Fruitland Park">Fruitland Park</option>
                    <option value="Gainesville">Gainesville</option>
                    <option value="Glen Ridge">Glen Ridge</option>
                    <option value="Glen St. Mary">Glen St. Mary</option>
                    <option value="Golden Beach">Golden Beach</option>
                    <option value="Golf">Golf</option>
                    <option value="Graceville">Graceville</option>
                    <option value="Grand Ridge">Grand Ridge</option>
                    <option value="Grant-Valkaria">Grant-Valkaria</option>
                    <option value="Green Cove Springs">Green Cove Springs</option>
                    <option value="Greenacres">Greenacres</option>
                    <option value="Greensboro">Greensboro</option>
                    <option value="Greenville">Greenville</option>
                    <option value="Greenwood">Greenwood</option>
                    <option value="Gretna">Gretna</option>
                    <option value="Groveland">Groveland</option>
                    <option value="Gulf Breeze">Gulf Breeze</option>
                    <option value="Gulf Stream">Gulf Stream</option>
                    <option value="Gulfport">Gulfport</option>
                    <option value="Haines City">Haines City</option>
                    <option value="Hallandale Beach">Hallandale Beach</option>
                    <option value="Hampton">Hampton</option>
                    <option value="Havana">Havana</option>
                    <option value="Haverhill">Haverhill</option>
                    <option value="Hawthorne">Hawthorne</option>
                    <option value="Hialeah">Hialeah</option>
                    <option value="Hialeah Gardens">Hialeah Gardens</option>
                    <option value="High Springs">High Springs</option>
                    <option value="Highland Beach">Highland Beach</option>
                    <option value="Highland Park">Highland Park</option>
                    <option value="Hillcrest Heights">Hillcrest Heights</option>
                    <option value="Hilliard">Hilliard</option>
                    <option value="Hillsboro Beach">Hillsboro Beach</option>
                    <option value="Holly Hill">Holly Hill</option>
                    <option value="Hollywood">Hollywood</option>
                    <option value="Holmes Beach">Holmes Beach</option>
                    <option value="Homestead">Homestead</option>
                    <option value="Horseshoe Beach">Horseshoe Beach</option>
                    <option value="Howey-in-the-Hills">Howey-in-the-Hills</option>
                    <option value="Hypoluxo">Hypoluxo</option>
                    <option value="Indialantic">Indialantic</option>
                    <option value="Indian Creek">Indian Creek</option>
                    <option value="Indian Harbour Beach">Indian Harbour Beach</option>
                    <option value="Indian River Shores">Indian River Shores</option>
                    <option value="Indian Rocks Beach">Indian Rocks Beach</option>
                    <option value="Indian Shores">Indian Shores</option>
                    <option value="Indiantown">Indiantown</option>
                    <option value="Inglis">Inglis</option>
                    <option value="Interlachen">Interlachen</option>
                    <option value="Inverness">Inverness</option>
                    <option value="Islamorada">Islamorada</option>
                    <option value="Jacksonville">Jacksonville</option>
                    <option value="Jacksonville Beach">Jacksonville Beach</option>
                    <option value="Jasper">Jasper</option>
                    <option value="Jennings">Jennings</option>
                    <option value="Juno Beach">Juno Beach</option>
                    <option value="Jupiter">Jupiter</option>
                    <option value="Jupiter Inlet Colony">Jupiter Inlet Colony</option>
                    <option value="Jupiter Island">Jupiter Island</option>
                    <option value="Kenneth City">Kenneth City</option>
                    <option value="Key Biscayne">Key Biscayne</option>
                    <option value="Key Colony Beach">Key Colony Beach</option>
                    <option value="Key West">Key West</option>
                    <option value="Kissimmee">Kissimmee</option>
                    <option value="Lady Lake">Lady Lake</option>
                    <option value="Lake Alfred">Lake Alfred</option>
                    <option value="Lake City">Lake City</option>
                    <option value="Lake Clarke Shores">Lake Clarke Shores</option>
                    <option value="Lake Hamilton">Lake Hamilton</option>
                    <option value="Lake Helen">Lake Helen</option>
                    <option value="Lake Mary">Lake Mary</option>
                    <option value="Lake Park">Lake Park</option>
                    <option value="Lake Placid">Lake Placid</option>
                    <option value="Lake Wales">Lake Wales</option>
                    <option value="Lake Worth Beach">Lake Worth Beach</option>
                    <option value="Lakeland">Lakeland</option>
                    <option value="Lantana">Lantana</option>
                    <option value="Largo">Largo</option>
                    <option value="Lauderdale Lakes">Lauderdale Lakes</option>
                    <option value="Lauderhill">Lauderhill</option>
                    <option value="Lawtey">Lawtey</option>
                    <option value="Leesburg">Leesburg</option>
                    <option value="Lighthouse Point">Lighthouse Point</option>
                    <option value="Live Oak">Live Oak</option>
                    <option value="Longboat Key">Longboat Key</option>
                    <option value="Longwood">Longwood</option>
                    <option value="Lynn Haven">Lynn Haven</option>
                    <option value="Macclenny">Macclenny</option>
                    <option value="Madeira Beach">Madeira Beach</option>
                    <option value="Madison">Madison</option>
                    <option value="Maitland">Maitland</option>
                    <option value="Manalapan">Manalapan</option>
                    <option value="Mangonia Park">Mangonia Park</option>
                    <option value="Marathon">Marathon</option>
                    <option value="Marco Island">Marco Island</option>
                    <option value="Margate">Margate</option>
                    <option value="Marianna">Marianna</option>
                    <option value="Mascotte">Mascotte</option>
                    <option value="Mayo">Mayo</option>
                    <option value="Melbourne">Melbourne</option>
                    <option value="Melbourne Beach">Melbourne Beach</option>
                    <option value="Miami">Miami</option>
                    <option value="Miami Beach">Miami Beach</option>
                    <option value="Miami Gardens">Miami Gardens</option>
                    <option value="Miami Lakes">Miami Lakes</option>
                    <option value="Miami Shores">Miami Shores</option>
                    <option value="Miami Springs">Miami Springs</option>
                    <option value="Micanopy">Micanopy</option>
                    <option value="Milton">Milton</option>
                    <option value="Miramar">Miramar</option>
                    <option value="Monticello">Monticello</option>
                    <option value="Mount Dora">Mount Dora</option>
                    <option value="Mulberry">Mulberry</option>
                    <option value="Naples">Naples</option>
                    <option value="Neptune Beach">Neptune Beach</option>
                    <option value="New Port Richey">New Port Richey</option>
                    <option value="New Smyrna Beach">New Smyrna Beach</option>
                    <option value="Newberry">Newberry</option>
                    <option value="Niceville">Niceville</option>
                    <option value="North Bay Village">North Bay Village</option>
                    <option value="North Lauderdale">North Lauderdale</option>
                    <option value="North Miami">North Miami</option>
                    <option value="North Miami Beach">North Miami Beach</option>
                    <option value="North Palm Beach">North Palm Beach</option>
                    <option value="North Port">North Port</option>
                    <option value="North Redington Beach">North Redington Beach</option>
                    <option value="Oakland">Oakland</option>
                    <option value="Oakland Park">Oakland Park</option>
                    <option value="Ocala">Ocala</option>
                    <option value="Ocean Ridge">Ocean Ridge</option>
                    <option value="Ocoee">Ocoee</option>
                    <option value="Oldsmar">Oldsmar</option>
                    <option value="Opa-locka">Opa-locka</option>
                    <option value="Orange City">Orange City</option>
                    <option value="Orange Park">Orange Park</option>
                    <option value="Orlando">Orlando</option>
                    <option value="Ormond Beach">Ormond Beach</option>
                    <option value="Oviedo">Oviedo</option>
                    <option value="Pahokee">Pahokee</option>
                    <option value="Palatka">Palatka</option>
                    <option value="Palm Bay">Palm Bay</option>
                    <option value="Palm Beach">Palm Beach</option>
                    <option value="Palm Beach Gardens">Palm Beach Gardens</option>
                    <option value="Palm Beach Shores">Palm Beach Shores</option>
                    <option value="Palm Coast">Palm Coast</option>
                    <option value="Palm Springs">Palm Springs</option>
                    <option value="Palmetto">Palmetto</option>
                    <option value="Panama City">Panama City</option>
                    <option value="Panama City Beach">Panama City Beach</option>
                    <option value="Parker">Parker</option>
                    <option value="Parkland">Parkland</option>
                    <option value="Pembroke Park">Pembroke Park</option>
                    <option value="Pembroke Pines">Pembroke Pines</option>
                    <option value="Perry">Perry</option>
                    <option value="Pinecrest">Pinecrest</option>
                    <option value="Pinellas Park">Pinellas Park</option>
                    <option value="Plant City">Plant City</option>
                    <option value="Polk City">Polk City</option>
                    <option value="Plantation">Plantation</option>
                    <option value="Pompano Beach">Pompano Beach</option>
                    <option value="Ponce Inlet">Ponce Inlet</option>
                    <option value="Port Orange">Port Orange</option>
                    <option value="Port Richey">Port Richey</option>
                    <option value="Port St. Joe">Port St. Joe</option>
                    <option value="Port St. Lucie">Port St. Lucie</option>
                    <option value="Punta Gorda">Punta Gorda</option>
                    <option value="Quincy">Quincy</option>
                    <option value="Redington Beach">Redington Beach</option>
                    <option value="Redington Shores">Redington Shores</option>
                    <option value="Riviera Beach">Riviera Beach</option>
                    <option value="Rockledge">Rockledge</option>
                    <option value="Royal Palm Beach">Royal Palm Beach</option>
                    <option value="Safety Harbor">Safety Harbor</option>
                    <option value="Sanford">Sanford</option>
                    <option value="Sanibel">Sanibel</option>
                    <option value="Sarasota">Sarasota</option>
                    <option value="Satellite Beach">Satellite Beach</option>
                    <option value="Sea Ranch Lakes">Sea Ranch Lakes</option>
                    <option value="Sebastian">Sebastian</option>
                    <option value="Sebring">Sebring</option>
                    <option value="Seminole">Seminole</option>
                    <option value="South Bay">South Bay</option>
                    <option value="South Daytona">South Daytona</option>
                    <option value="South Miami">South Miami</option>
                    <option value="South Palm Beach">South Palm Beach</option>
                    <option value="South Pasadena">South Pasadena</option>
                    <option value="Springfield">Springfield</option>
                    <option value="St. Augustine">St. Augustine</option>
                    <option value="St. Augustine Beach">St. Augustine Beach</option>
                    <option value="St. Cloud">St. Cloud</option>
                    <option value="St. Pete Beach">St. Pete Beach</option>
                    <option value="St. Petersburg">St. Petersburg</option>
                    <option value="Starke">Starke</option>
                    <option value="Stuart">Stuart</option>
                    <option value="Sunny Isles Beach">Sunny Isles Beach</option>
                    <option value="Sunrise">Sunrise</option>
                    <option value="Surfside">Surfside</option>
                    <option value="Sweetwater">Sweetwater</option>
                    <option value="Tallahassee">Tallahassee</option>
                    <option value="Tamarac">Tamarac</option>
                    <option value="Tampa">Tampa</option>
                    <option value="Tarpon Springs">Tarpon Springs</option>
                    <option value="Tavares">Tavares</option>
                    <option value="Temple Terrace">Temple Terrace</option>
                    <option value="Tequesta">Tequesta</option>
                    <option value="Titusville">Titusville</option>
                    <option value="Treasure Island">Treasure Island</option>
                    <option value="Trenton">Trenton</option>
                    <option value="Umatilla">Umatilla</option>
                    <option value="Valparaiso">Valparaiso</option>
                    <option value="Venice">Venice</option>
                    <option value="Vernon">Vernon</option>
                    <option value="Vero Beach">Vero Beach</option>
                    <option value="Virginia Gardens">Virginia Gardens</option>
                    <option value="Waldo">Waldo</option>
                    <option value="Wauchula">Wauchula</option>
                    <option value="Wausau">Wausau</option>
                    <option value="Webster">Webster</option>
                    <option value="Welaka">Welaka</option>
                    <option value="Wellington">Wellington</option>
                    <option value="Westlake">Westlake</option>
                    <option value="West Melbourne">West Melbourne</option>
                    <option value="West Miami">West Miami</option>
                    <option value="West Palm Beach">West Palm Beach</option>
                    <option value="West Park">West Park</option>
                    <option value="Weston">Weston</option>
                    <option value="Westville">Westville</option>
                    <option value="Wewahitchka">Wewahitchka</option>
                    <option value="White Springs">White Springs</option>
                    <option value="Wildwood">Wildwood</option>
                    <option value="Williston">Williston</option>
                    <option value="Wilton Manors">Wilton Manors</option>
                    <option value="Windermere">Windermere</option>
                    <option value="Winter Garden">Winter Garden</option>
                    <option value="Winter Haven">Winter Haven</option>
                    <option value="Winter Park">Winter Park</option>
                    <option value="Winter Springs">Winter Springs</option>
                    <option value="Worthington Springs">Worthington Springs</option>
                    <option value="Yankeetown">Yankeetown</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={getValue('contact_city')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Address of Alien Contact:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="contactStreetAddress"
                    value={getValue('contact_street_address')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="House number and street name"
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('contact_street_address')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('contact_street_address') ? "House number and street name" : ""}
                  />
                )}
              </div>
            </div>



            <h3>6. Suspected Alien's Personal Information</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's First Name:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="firstName"
                    value={getValue('first_name')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('first_name')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Alien's Middle Name:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="middleName"
                    value={getValue('middle_name')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('middle_name')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Alien's Last Name:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="lastName"
                    value={getValue('last_name')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('last_name')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's Country of Citizenship:</span>
                {isEditing ? (
                  <select
                    name="countryOfCitizenship"
                    value={getValue('country_of_citizenship')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">-- Select an option --</option>
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.label}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={getValue('country_of_citizenship')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.label}>
                        {country.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Alien's Registration Number:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="alienRegistrationNumber"
                    value={getValue('alien_registration_number')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    pattern="[0-9]*"
                    inputMode="numeric"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '');
                      handleInputChange(e);
                    }}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('alien_registration_number')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('alien_registration_number') ? "Numbers only" : ""}
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Has Dual Citizenship:</span>
                {isEditing ? (
                  <input
                    type="checkbox"
                    name="hasDualCitizenship"
                    checked={getValue('has_dual_citizenship')}
                    onChange={handleInputChange}
                    className="modern-checkbox"
                  />
                ) : (
                  <input
                    type="checkbox"
                    checked={getValue('has_dual_citizenship')}
                    className="modern-checkbox"
                    style={viewOnlyFieldStyle}
                    disabled
                  />
                )}
              </div>
              {/* Show dual citizenship country field in edit mode only when checkbox is checked, but always show in view mode */}
              {(isEditing ? getValue('has_dual_citizenship') : true) && (
                <div className="detailItem">
                  <span className="detailLabel">Dual Citizenship Country:</span>
                  {isEditing ? (
                    <select
                      name="dualCitizenshipCountry"
                      value={getValue('dual_citizenship_country')}
                      onChange={handleInputChange}
                      className="modern-input"
                      style={editableFieldStyle}
                    >
                      <option value="">-- Select an option --</option>
                      {COUNTRIES.map(country => (
                        <option key={country.value} value={country.label}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={getValue('dual_citizenship_country')}
                      className="modern-input"
                      style={viewOnlyFieldStyle}
                      disabled
                    >
                      <option value="">-- Select an option --</option>
                      {COUNTRIES.map(country => (
                        <option key={country.value} value={country.label}>
                          {country.label}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              )}
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's Date of Birth:</span>
                {isEditing ? (
                  <input
                    type="date"
                    name="dateOfBirth"
                    value={formatDateForInput(getValue('date_of_birth'))}
                    onChange={handleDateInputChange}
                    onFocus={handleDateFocus}
                    onClick={handleDateClick}
                    className="modern-input"
                    style={editableFieldStyle}
                    min="1000-01-01"
                    max="9999-12-31"
                  />
                ) : (
                  <input
                    type="date"
                    value={formatDateForInput(getValue('date_of_birth'))}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Alien's Sex:</span>
                {isEditing ? (
                  <select
                    name="sex"
                    value={getValue('sex')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                ) : (
                  <select
                    value={getValue('sex')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                )}
              </div>
            </div>

            <h3>7. Suspected Alien's Current Address</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's Current Street Address:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="currentStreetAddress"
                    value={getValue('current_street_address')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('current_street_address')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's Current City:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="currentCity"
                    value={getValue('current_city')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('current_city')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Alien's Current State:</span>
                {isEditing ? (
                  <select
                    name="currentState"
                    value={getValue('current_state')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">-- Select an option --</option>
                    {US_STATES.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={getValue('current_state')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    {US_STATES.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Alien's Current Zip Code:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="currentZipCode"
                    value={getValue('current_zip_code')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    pattern="[0-9]{5}"
                    inputMode="numeric"
                    maxLength="5"
                    placeholder="5 digits only"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
                      handleInputChange(e);
                    }}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('current_zip_code')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('current_zip_code') ? "5 digits only" : ""}
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's Phone Number:</span>
                {isEditing ? (
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formatPhoneForDisplay(getValue('phone_number'))}
                    onChange={(e) => {
                      // Format phone number as XXX-XXX-XXXX
                      let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
                      // Limit to 10 digits
                      value = value.slice(0, 10);
                      // Format as XXX-XXX-XXXX
                      if (value.length >= 6) {
                        value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6);
                      } else if (value.length >= 3) {
                        value = value.slice(0, 3) + '-' + value.slice(3);
                      }

                      const syntheticEvent = {
                        target: {
                          name: 'phoneNumber',
                          value: value,
                          type: 'tel'
                        }
                      };
                      handleInputChange(syntheticEvent);
                    }}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="555-555-5555"
                    title="Enter valid 10-digit phone number XXX-XXX-XXXX"
                  />
                ) : (
                  <input
                    type="tel"
                    value={formatPhoneForDisplay(getValue('phone_number'))}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('phone_number') ? "555-555-5555" : ""}
                  />
                )}
              </div>
            </div>

            <h3>8. Vehicle Information</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Vehicle Year:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="vehicleYear"
                    value={getValue('vehicle_year')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="YYYY (e.g., 2020)"
                    pattern="[0-9]{4}"
                    maxLength="4"
                    title="Please enter a 4-digit year"
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('vehicle_year')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('vehicle_year') ? "YYYY (e.g., 2020)" : ""}
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Vehicle Make:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="vehicleMake"
                    value={getValue('vehicle_make')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="Toyota, Ford, etc."
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('vehicle_make')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('vehicle_make') ? "Toyota, Ford, etc." : ""}
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Vehicle Model:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="vehicleModel"
                    value={getValue('vehicle_model')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="Camry, F-150, etc."
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('vehicle_model')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('vehicle_model') ? "Camry, F-150, etc." : ""}
                  />
                )}
              </div>
            </div>

            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Vehicle Tag:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="vehicleTag"
                    value={getValue('vehicle_tag')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="License plate number"
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('vehicle_tag')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('vehicle_tag') ? "License plate number" : ""}
                  />
                )}
              </div>
              <div className="detailItem">
                <span className="detailLabel">Vehicle Tag State:</span>
                {isEditing ? (
                  <select
                    name="vehicleTagState"
                    value={getValue('vehicle_tag_state')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">-- Select an option --</option>
                    {US_STATES.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={getValue('vehicle_tag_state')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    {US_STATES.map(state => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>

            <h3>9. Suspected Alien's Current Employer Info</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's Current Employer:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="employerName"
                    value={getValue('employer_name')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('employer_name')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Alien's Current Employer's Street Address:</span>
                {isEditing ? (
                  <input
                    type="text"
                    name="employerStreetAddress"
                    value={getValue('employer_street_address')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    placeholder="Include Street Address, City and State - Ex: 1234 Test Street, Miami, Florida - Do not use abbreviations"
                  />
                ) : (
                  <input
                    type="text"
                    value={getValue('employer_street_address')}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                    placeholder={getValue('employer_street_address') ? "Include Street Address, City and State - Ex: 1234 Test Street, Miami, Florida - Do not use abbreviations" : ""}
                  />
                )}
              </div>
            </div>




            <h3>10. Additional Information</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Does Alien Have Gang Affiliations:</span>
                {isEditing ? (
                  <select
                    name="hasGangAffiliations"
                    value={getValue('has_gang_affiliations') === true ? 'Yes' : getValue('has_gang_affiliations') === false ? 'No' : ''}
                    onChange={(e) => handleInputChange({
                      target: {
                        name: 'hasGangAffiliations',
                        type: 'checkbox',
                        checked: e.target.value === 'Yes'
                      }
                    })}
                    className="modern-input"
                    required
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : (
                  <select
                    value={getValue('has_gang_affiliations') === true ? 'Yes' : getValue('has_gang_affiliations') === false ? 'No' : ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                )}
              </div>
              {/* Show gang name field in edit mode only when checkbox is checked, but always show in view mode */}
              {(isEditing ? getValue('has_gang_affiliations') : true) && (
                <div className="detailItem">
                  <span className="detailLabel">Gang Name:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      name="gangName"
                      value={getValue('gang_name')}
                      onChange={handleInputChange}
                      className="modern-input"
                      style={editableFieldStyle}
                    />
                  ) : (
                    <input
                      type="text"
                      value={getValue('gang_name')}
                      className="modern-input"
                      style={viewOnlyFieldStyle}
                      readOnly
                      disabled
                    />
                  )}
                </div>
              )}
            </div>


            <div className="detailRow">
              <div className="detailItem fullWidth">
                <span className="detailLabel">Description of Contact or Administrative Actions Taken:</span>
                {isEditing ? (
                  <textarea
                    name="contactDescription"
                    value={getValue('contact_description')}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    rows="4"
                    placeholder="Describe the contact or administrative actions taken during this encounter..."
                  />
                ) : (
                  <textarea
                    value={getValue('contact_description') || ''}
                    className="modern-input"
                    style={{...viewOnlyFieldStyle, width: '100%', minHeight: '80px'}}
                    rows="4"
                    readOnly
                    disabled
                    placeholder={getValue('contact_description') ? "Describe the contact or administrative actions taken during this encounter..." : ""}
                  />
                )}
              </div>
            </div>


            <div className="detailRow">
              <div className="detailItem fullWidth">
                <span className="detailLabel">Was Contact Made with Any Federal Agency Regarding This Alien Contact:</span>
                {isEditing ? (
                  <select
                    name="federalAgencyContact"
                    value={getValue('federal_agency_contact') || ''}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Immigration and Customs Enforcement Homeland Security Investigations">Immigration and Customs Enforcement Homeland Security Investigations</option>
                    <option value="Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations">Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations</option>
                    <option value="Customs and Border Protection">Customs and Border Protection</option>
                    <option value="Customs and Border Protection United States Border Patrol">Customs and Border Protection United States Border Patrol</option>
                    <option value="Customs and Border Protection Air and Marine Operations">Customs and Border Protection Air and Marine Operations</option>
                    <option value="Customs and Border Protection Office of Field Operations">Customs and Border Protection Office of Field Operations</option>
                    <option value="United States Coast Guard">United States Coast Guard</option>
                    <option value="No federal agency contacted">No federal agency contacted</option>
                  </select>
                ) : (
                  <select
                    value={getValue('federal_agency_contact') || ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Immigration and Customs Enforcement Homeland Security Investigations">Immigration and Customs Enforcement Homeland Security Investigations</option>
                    <option value="Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations">Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations</option>
                    <option value="Customs and Border Protection">Customs and Border Protection</option>
                    <option value="Customs and Border Protection Air and Marine Operations">Customs and Border Protection Air and Marine Operations</option>
                    <option value="Customs and Border Protection Office of Field Operations">Customs and Border Protection Office of Field Operations</option>
                    <option value="United States Coast Guard">United States Coast Guard</option>
                    <option value="No federal agency contacted">No federal agency contacted</option>
                  </select>
                )}
              </div>
            </div>


            <div className="detailRow">
              <div className="detailItem fullWidth">
                <span className="detailLabel">Alien Contact was in Collaboration with which Agency (choose lead agency):</span>
                {isEditing ? (
                  <select
                    name="collaborationAgency"
                    value={getValue('collaboration_agency') || ''}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Not in Collaboration with an Agency">Not in Collaboration with an Agency</option>
                    <option value="Other Local Agency Only">Other Local Agency Only</option>
                    <option value="Other State Agency Only">Other State Agency Only</option>
                    <option value="Immigration and Customs Enforcement Homeland Security Investigations">Immigration and Customs Enforcement Homeland Security Investigations</option>
                    <option value="Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations">Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations</option>
                    <option value="Customs and Border Protection">Customs and Border Protection</option>
                    <option value="Customs and Border Protection United States Border Patrol">Customs and Border Protection United States Border Patrol</option>
                    <option value="Customs and Border Protection Air and Marine Operations">Customs and Border Protection Air and Marine Operations</option>
                    <option value="Customs and Border Protection Office of Field Operations">Customs and Border Protection Office of Field Operations</option>
                    <option value="United States Coast Guard">United States Coast Guard</option>
                  </select>
                ) : (
                  <select
                    value={getValue('collaboration_agency') || ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Not in Collaboration with an Agency">Not in Collaboration with an Agency</option>
                    <option value="Other Local Agency Only">Other Local Agency Only</option>
                    <option value="Other State Agency Only">Other State Agency Only</option>
                    <option value="Other Federal Agency Only">Other Federal Agency Only</option>
                    <option value="Immigration and Customs Enforcement Homeland Security Investigations">Immigration and Customs Enforcement Homeland Security Investigations</option>
                    <option value="Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations">Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations</option>
                    <option value="Customs and Border Protection">Customs and Border Protection</option>
                    <option value="Customs and Border Protection Air and Marine Operations">Customs and Border Protection Air and Marine Operations</option>
                    <option value="Customs and Border Protection Office of Field Operations">Customs and Border Protection Office of Field Operations</option>
                    <option value="United States Coast Guard">United States Coast Guard</option>
                  </select>
                )}
              </div>
            </div>


            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Does Alien Have a Criminal History:</span>
                {isEditing ? (
                  <select
                    name="hasCriminalHistory"
                    value={(() => {
                      const value = getValue('has_criminal_history');
                      // Handle both new string values and legacy boolean values
                      if (typeof value === 'string') {
                        return ['Yes', 'No', 'Unknown'].includes(value) ? value : '';
                      } else if (typeof value === 'boolean') {
                        return value ? 'Yes' : 'No';
                      } else if (value === null || value === undefined) {
                        return '';
                      } else {
                        return 'Unknown';
                      }
                    })()}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleInputChange({
                        target: {
                          name: 'hasCriminalHistory',
                          type: 'select',
                          value: value || undefined // Store the string value directly
                        }
                      });
                    }}
                    className="modern-input"
                    required
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unknown">Unknown</option>
                  </select>
                ) : (
                  <select
                    value={(() => {
                      const value = getValue('has_criminal_history');
                      // Handle both new string values and legacy boolean values
                      if (typeof value === 'string') {
                        return ['Yes', 'No', 'Unknown'].includes(value) ? value : '';
                      } else if (typeof value === 'boolean') {
                        return value ? 'Yes' : 'No';
                      } else {
                        return '';
                      }
                    })()}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                    <option value="Unknown">Unknown</option>
                  </select>
                )}
              </div>
            </div>





            <h3>11. Citizenship Status Verification</h3>
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Was Citizenship Status Verified with ICE Call Center:</span>
                {isEditing ? (
                  <select
                    name="citizenshipVerifiedWithIce"
                    value={getValue('citizenship_verified_with_ice') || ''}
                    onChange={handleInputChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    required
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : (
                  <select
                    value={getValue('citizenship_verified_with_ice') || ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                )}
              </div>
            </div>
            {/* Show ICE call center contact field in edit mode only when citizenship verified, but always show in view mode */}
            {(isEditing ? getValue('citizenship_verified_with_ice') === 'Yes' : true) && (
              <>
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Name and Identification of Call Center Contact:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="iceCallCenterContact"
                        value={getValue('ice_call_center_contact')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                        placeholder="NA if Citizenship was Not Verified"
                        required
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('ice_call_center_contact')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                        placeholder={getValue('ice_call_center_contact') ? "NA if Citizenship was Not Verified" : ""}
                      />
                    )}
                  </div>
                </div>
              </>
            )}


            {/* 12.1. ICE Contact Status - FDLE Field el-42 */}
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Was ICE Contacted:</span>
                {isEditing ? (
                  <select
                    name="iceContacted"
                    value={getValue('ice_contacted') || ''}
                    onChange={handleInputChange}
                    className="modern-input"
                    required
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                ) : (
                  <select
                    value={getValue('ice_contacted') || ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                )}
              </div>
            </div>
            {/* 12.2. ICE Response Status - FDLE Field el-43 - Show when ICE contacted in edit mode, always show in view mode */}
            {(isEditing ? getValue('ice_contacted') === 'Yes' : true) && (
              <>
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">If ICE Contacted: Did ICE Respond:</span>
                    {isEditing ? (
                      <select
                        name="iceResponded"
                        value={getValue('ice_responded') || ''}
                        onChange={handleInputChange}
                        className="modern-input"
                        required
                      >
                        <option value="">-- Select an option --</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="ICE was not contacted">ICE was not contacted</option>
                      </select>
                    ) : (
                      <select
                        value={getValue('ice_responded') || ''}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        disabled
                      >
                        <option value="">-- Select an option --</option>
                        <option value="Yes">Yes</option>
                        <option value="No">No</option>
                        <option value="ICE was not contacted">ICE was not contacted</option>
                      </select>
                    )}
                  </div>
                </div>
                {/* 12.3. ICE Release Status - FDLE Field el-45 - Show when ICE responded in edit mode, always show in view mode */}
                {(isEditing ? getValue('ice_responded') === 'Yes' : true) && (
                  <div className="detailRow">
                    <div className="detailItem">
                      <span className="detailLabel">If ICE Responded: Was Alien Released by ICE:</span>
                      {isEditing ? (
                        <select
                          name="alienReleasedByIce"
                          value={getValue('alien_released_by_ice') || ''}
                          onChange={handleInputChange}
                          className="modern-input"
                          style={editableFieldStyle}
                          required
                        >
                          <option value="">-- Select an option --</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="ICE did not respond">ICE did not respond</option>
                        </select>
                      ) : (
                        <select
                          value={getValue('alien_released_by_ice') || ''}
                          className="modern-input"
                          style={viewOnlyFieldStyle}
                          disabled
                        >
                          <option value="">-- Select an option --</option>
                          <option value="Yes">Yes</option>
                          <option value="No">No</option>
                          <option value="ICE did not respond">ICE did not respond</option>
                        </select>
                      )}
                    </div>
                  </div>
                )}
                {/* Show not detained reason when ICE responded and alien was not released in edit mode, always show in view mode */}
                {(isEditing ? (getValue('ice_responded') === 'Yes' && getValue('alien_released_by_ice') === 'No') : true) && (
                  <div className="detailRow">
                    <div className="detailItem">
                      <span className="detailLabel">
                        Why not detained?
                        {isEditing && <span style={{fontSize: '0.85em', color: '#666', marginLeft: '8px'}}>Max 100 characters: ({getValue('not_detained_reason')?.length || 0}/100)</span>}
                      </span>
                      {isEditing ? (
                        <input
                          type="text"
                          name="notDetainedReason"
                          value={getValue('not_detained_reason')}
                          onChange={handleInputChange}
                          maxLength="100"
                          className="modern-input"
                          style={{...editableFieldStyle, width: '100%'}}
                        />
                      ) : (
                        <input
                          type="text"
                          value={getValue('not_detained_reason')}
                          className="modern-input"
                          style={{...viewOnlyFieldStyle, width: '100%'}}
                          readOnly
                          disabled
                        />
                      )}
                    </div>
                  </div>
                )}
                {/* 12.4. ICE No Response Reason - Show when ICE was contacted but didn't respond in edit mode, always show in view mode */}
                {(isEditing ? ((getValue('ice_contacted') === 'Yes' || getValue('ice_contacted') === 'Arrest was made') && getValue('ice_responded') === 'No') : true) && (
                  <div className="detailRow">
                    <div className="detailItem">
                      <span className="detailLabel">
                        Why did ICE not respond?
                        {isEditing && <span style={{fontSize: '0.85em', color: '#666', marginLeft: '8px'}}>Max 100 characters: ({getValue('ice_no_response_reason')?.length || 0}/100)</span>}
                      </span>
                      {isEditing ? (
                        <input
                          type="text"
                          name="iceNoResponseReason"
                          value={getValue('ice_no_response_reason')}
                          onChange={handleInputChange}
                          maxLength="100"
                          className="modern-input"
                          style={{...editableFieldStyle, width: '100%'}}
                        />
                      ) : (
                        <input
                          value={getValue('ice_no_response_reason')}
                          className="modern-input"
                          style={{...viewOnlyFieldStyle, width: '100%'}}
                          readOnly
                          disabled
                        />
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            <h3>12. Arrest Information</h3>

            {/* 11.1. Was Alien Arrested During This Contact */}
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Was Alien Arrested During This Contact:</span>
                {isEditing ? (
                  <select
                    name="wasArrested"
                    value={getValue('was_arrested') || ''}
                    onChange={handleInputChange}
                    className="modern-input"
                    required
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes - Local/State Charges">Yes - Local/State Charges</option>
                    <option value="Yes - Federal Immigration Charges">Yes - Federal Immigration Charges</option>
                    <option value="Not Arrested">Not Arrested</option>
                  </select>
                ) : (
                  <select
                    value={getValue('was_arrested') || ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    disabled
                  >
                    <option value="">-- Select an option --</option>
                    <option value="Yes - Local/State Charges">Yes - Local/State Charges</option>
                    <option value="Yes - Federal Immigration Charges">Yes - Federal Immigration Charges</option>
                    <option value="Not Arrested">Not Arrested</option>
                  </select>
                )}
              </div>
            </div>

            {/* 11.2. 287(g) Authority Usage */}
            {getValue('was_arrested') ? (
              <div className="detailRow">
                <div className="detailItem">
                  <span className="detailLabel">If Arrested, 287(g) Authority Was Used for this Arrest:</span>
                  {isEditing ? (
                    <select
                      name="used287gAuthority"
                      value={getValue('used_287g_authority') || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleInputChange({
                          target: {
                            name: 'used287gAuthority',
                            type: 'select',
                            value: value || null
                          }
                        });
                      }}
                      className="modern-input"
                      required
                    >
                      <option value="">-- Select an option --</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Alien was not arrested">Alien was not arrested</option>
                    </select>
                  ) : (
                    <select
                      value={getValue('used_287g_authority') || ''}
                      className="modern-input"
                      style={viewOnlyFieldStyle}
                      disabled
                    >
                      <option value="">-- Select an option --</option>
                      <option value="Yes">Yes</option>
                      <option value="No">No</option>
                      <option value="Alien was not arrested">Alien was not arrested</option>
                    </select>
                  )}
                </div>
              </div>
            ) : (
              <div className="detailRow">
                <div className="detailItem">
                  <span className="detailLabel">If Arrested, 287(g) Authority Was Used for this Arrest:</span>
                  {isEditing ? (
                    <input
                      type="text"
                      value="Alien was not arrested"
                      className="modern-input"
                      disabled
                      style={{ backgroundColor: '#f8f9fa', color: '#6c757d' }}
                    />
                  ) : (
                    <input
                      type="text"
                      value="Alien was not arrested"
                      className="modern-input"
                      style={viewOnlyFieldStyle}
                      readOnly
                      disabled
                    />
                  )}
                </div>
              </div>
            )}

            {/* 11.3. Arrest Charges */}
            {/* Show arrest charges field in edit mode only when arrested, but always show in view mode */}
            {(isEditing ? getValue('was_arrested') : true) && (
              <div className="detailRow">
                <div className="detailItem">
                  <span className="detailLabel">Arrest Charges (in order of severity):</span>
                  {isEditing ? (
                    <textarea
                      name="arrestCharges"
                      value={getValue('arrest_charges')}
                      onChange={handleInputChange}
                      className="modern-input"
                      style={{...editableFieldStyle, width: '100%', minHeight: '60px'}}
                      rows="3"
                    />
                  ) : (
                    <textarea
                      value={getValue('arrest_charges')}
                      className="modern-input"
                      style={{...viewOnlyFieldStyle, width: '100%', minHeight: '60px'}}
                      rows="3"
                      readOnly
                      disabled
                    />
                  )}
                </div>
              </div>
            )}

            <h3>13. Photo</h3>
            {getValue('photo_url') && (
              <div className="detailRow">
                <div className="detailItem">
                  {(() => {
                    const photoUrl = getValue('photo_url');
                    let finalUrl;

                    if (photoUrl.startsWith('http')) {
                      // Already a full URL (signed URL from S3)
                      finalUrl = photoUrl;
                    } else if (photoUrl.startsWith('/api/image/')) {
                      // Path already includes /api/image/ prefix
                      finalUrl = process.env.NODE_ENV === 'production'
                        ? photoUrl
                        : `http://localhost:8080${photoUrl}`;
                    } else if (photoUrl.startsWith('/uploads/')) {
                      // Local file path
                      finalUrl = process.env.NODE_ENV === 'production'
                        ? photoUrl
                        : `http://localhost:8080${photoUrl}`;
                    } else {
                      // Assume it's an S3 key that needs /api/image/ prefix
                      const imagePath = `/api/image/${photoUrl}`;
                      finalUrl = process.env.NODE_ENV === 'production'
                        ? imagePath
                        : `http://localhost:8080${imagePath}`;
                    }



                    // Check for problematic photo URLs
                    if (photoUrl === 'immigrant' || photoUrl === 'immigrant.jpg' || photoUrl === 'immigrant.png') {
                      console.error(' BROKEN PHOTO URL DETECTED:', {
                        photoUrl,
                        recordId: editableRecord?.id,
                        recordName: `${editableRecord?.firstName} ${editableRecord?.lastName}`,
                        message: 'This record has a corrupted photo URL'
                      });
                    }

                    return (
                      <img
                        src={finalUrl}
                        alt="Immigrant"
                        style={{
                          width: '300px',
                          height: '300px',
                          objectFit: 'cover',
                          marginTop: '10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px'
                        }}
                        onError={(e) => {
                          console.error(' Image failed to load:', finalUrl);
                          console.error('Error event:', e);
                          console.error('Original photo URL from database:', photoUrl);

                          // Check if this is an image loading issue that might be temporary
                          const shouldRetry = finalUrl.includes('/api/image/') &&
                                            !e.target.hasAttribute('data-retry-attempted');

                          if (shouldRetry) {
                            console.log('🔄 Attempting to reload image...');
                            e.target.setAttribute('data-retry-attempted', 'true');

                            // Generate a new cache-busted URL and retry
                            const newCacheBuster = `?t=${Date.now()}`;
                            const baseUrl = finalUrl.split('?')[0]; // Remove existing cache buster
                            const retryUrl = `${baseUrl}${newCacheBuster}`;

                            console.log('🔄 Retrying with URL:', retryUrl);
                            e.target.src = retryUrl;
                            return; // Don't show error message yet, let retry attempt complete
                          }

                          // Hide the broken image and show an error message
                          e.target.style.display = 'none';

                          // Create error message if it doesn't exist
                          if (!e.target.nextSibling || !e.target.nextSibling.classList?.contains('image-error')) {
                            const errorDiv = document.createElement('div');
                            errorDiv.className = 'image-error';
                            errorDiv.style.cssText = `
                              padding: 20px;
                              border: 2px dashed #ccc;
                              border-radius: 4px;
                              text-align: center;
                              color: #666;
                              background-color: #f9f9f9;
                              margin-top: 10px;
                            `;
                            errorDiv.innerHTML = `
                              <div style="font-size: 48px; margin-bottom: 10px;">📷</div>
                              <div><strong>Image temporarily unavailable</strong></div>
                              <div style="font-size: 12px; margin-top: 5px;">
                                ${shouldRetry ?
                                  'The image could not be loaded. Please try refreshing the page.' :
                                  'This may be due to server maintenance or the image file may be missing.'}
                              </div>
                            `;
                            e.target.parentNode.insertBefore(errorDiv, e.target.nextSibling);
                          }
                        }}
                        onLoad={() => {
                          // Image loaded successfully
                        }}
                      />
                    );
                  })()}
                </div>
              </div>
            )}
            {isEditing && (
              <div className="detailRow">
                <div className="detailItem">
                  <span className="detailLabel">
                    {getValue('photo_url') ? 'Replace Photo:' : 'Upload Photo:'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoFileChange}
                    className="modern-input"
                    style={editableFieldStyle}
                    disabled={uploadingPhoto}
                  />
                  <div style={{ marginTop: '8px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                    Note: Make sure the photo is clear, and subject is identifiable from the neck up.
                  </div>
                  {uploadingPhoto && (
                    <div style={{ marginTop: '5px', fontSize: '14px', color: '#666' }}>
                      Uploading photo...
                    </div>
                  )}
                  {selectedPhotoFile && (
                    <div style={{ marginTop: '5px', fontSize: '14px', color: '#666' }}>
                      Selected: {selectedPhotoFile.name}
                    </div>
                  )}
                </div>
              </div>
            )}
            {!getValue('photo_url') && !isEditing && (
              <div className="detailRow">
                <div className="detailItem">
                  <input
                    type="text"
                    value="No photo available"
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                </div>
              </div>
            )}

            {/* DIO Investigation Details Header */}
            <h2 style={{
              color: 'var(--primary-color)',
              fontSize: '20px',
              marginTop: '30px',
              marginBottom: '20px',
              textAlign: 'left',
              borderBottom: '2px solid var(--primary-color)',
              paddingBottom: '10px'
            }}>
              DIO Investigation Details
            </h2>

            {/* Is Submitter DIO Field */}
            <div className="detailRow">
              <div className="detailItem">
                <span className="detailLabel">Is submitter DIO?:</span>
                {isEditing ? (
                  <select
                    name="is_submitter_dio"
                    value={getValue('is_submitter_dio') === true ? "true" : getValue('is_submitter_dio') === false ? "false" : ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const booleanValue = value === "true" ? true : value === "false" ? false : "";
                      const syntheticEvent = {
                        target: {
                          name: 'is_submitter_dio',
                          value: booleanValue,
                          type: 'select'
                        }
                      };
                      handleInputChange(syntheticEvent);
                    }}
                    className="modern-input"
                    style={editableFieldStyle}
                  >
                    <option value="">Select...</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <input
                    type="text"
                    value={getValue('is_submitter_dio') === true ? 'Yes' : getValue('is_submitter_dio') === false ? 'No' : ''}
                    className="modern-input"
                    style={viewOnlyFieldStyle}
                    readOnly
                    disabled
                  />
                )}
              </div>
            </div>

            {/* Non-Criminal Arrest Field - Only show when DIO is Yes */}
            {getValue('is_submitter_dio') === true && (
              <div className="detailRow">
                <div className="detailItem">
                  <span className="detailLabel">Non-Criminal Arrest?:</span>
                  {isEditing ? (
                    <select
                      name="is_non_criminal_arrest"
                      value={getValue('is_non_criminal_arrest') === true ? "true" : getValue('is_non_criminal_arrest') === false ? "false" : ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        const booleanValue = value === "true" ? true : value === "false" ? false : "";
                        const syntheticEvent = {
                          target: {
                            name: 'is_non_criminal_arrest',
                            value: booleanValue,
                            type: 'select'
                          }
                        };
                        handleInputChange(syntheticEvent);
                      }}
                      className="modern-input"
                      style={editableFieldStyle}
                    >
                      <option value="">Select...</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={getValue('is_non_criminal_arrest') === true ? 'Yes' : getValue('is_non_criminal_arrest') === false ? 'No' : ''}
                      className="modern-input"
                      style={viewOnlyFieldStyle}
                      readOnly
                      disabled
                    />
                  )}
                </div>
              </div>
            )}

            {/* TFM Arrest Sheet Fields - Only show when DIO is Yes AND Non-Criminal Arrest is Yes */}
            {getValue('is_submitter_dio') === true && getValue('is_non_criminal_arrest') === true && (
              <>
                <h2 style={{
                  color: 'var(--primary-color)',
                  fontSize: '20px',
                  marginTop: '30px',
                  marginBottom: '20px',
                  textAlign: 'left',
                  borderBottom: '2px solid var(--primary-color)',
                  paddingBottom: '10px'
                }}>
                  TFM Arrest Sheet
                </h2>

                {/* 1. Law Enforcement Agency */}
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Law Enforcement Agency:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmLawEnforcementAgency"
                        value={getValue('tfm_law_enforcement_agency')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_law_enforcement_agency')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                {/* 2. 287(g) Task Force Officer Name */}
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">287(g) Task Force Officer Name:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmTaskForceOfficerName"
                        value={getValue('tfm_task_force_officer_name')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_task_force_officer_name')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                {/* 3. Badge Number */}
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Badge Number:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmBadgeNumber"
                        value={getValue('tfm_badge_number')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_badge_number')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                {/* 4. ICE Credential (serial) number */}
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">ICE Credential (serial) number:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmIceCredentialNumber"
                        value={getValue('tfm_ice_credential_number')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_ice_credential_number')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                {/* 5. Cell Phone Number */}
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Cell Phone Number:</span>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="tfmCellPhone"
                        value={getValue('tfm_cell_phone')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_cell_phone')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                {/* 6. Date of Arrest & Arrest Time */}
                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Name:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmAlienName"
                        value={getValue('tfm_alien_name')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_alien_name')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">DOB:</span>
                    {isEditing ? (
                      <input
                        type="date"
                        name="tfmAlienDob"
                        value={getValue('tfm_alien_dob')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_alien_dob')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">A Number:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmAlienANumber"
                        value={getValue('tfm_alien_a_number')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_alien_a_number')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">Sex:</span>
                    {isEditing ? (
                      <select
                        name="tfmAlienSex"
                        value={getValue('tfm_alien_sex')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      >
                        <option value="">-- Select an option --</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_alien_sex')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Nationality:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmAlienNationality"
                        value={getValue('tfm_alien_nationality')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_alien_nationality')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">FBI Number:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmAlienFbiNumber"
                        value={getValue('tfm_alien_fbi_number')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_alien_fbi_number')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                {/* Task Force Officer Information Section */}
                <h3 style={{ marginTop: '20px', marginBottom: '15px', color: '#333' }}>Task Force Officer Information</h3>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Task Force Officer Name:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmTaskForceOfficerName"
                        value={getValue('tfm_task_force_officer_name')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_task_force_officer_name')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">Badge Number:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmBadgeNumber"
                        value={getValue('tfm_badge_number')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_badge_number')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">ICE Credential (Serial) Number:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmIceCredentialNumber"
                        value={getValue('tfm_ice_credential_number')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_ice_credential_number')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">Cell Phone Number:</span>
                    {isEditing ? (
                      <input
                        type="tel"
                        name="tfmCellPhone"
                        value={getValue('tfm_cell_phone')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_cell_phone')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Date of Arrest:</span>
                    {isEditing ? (
                      <input
                        type="date"
                        name="tfmArrestDate"
                        value={getValue('tfm_arrest_date')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_arrest_date')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">Arrest Time:</span>
                    {isEditing ? (
                      <input
                        type="time"
                        name="tfmArrestTime"
                        value={getValue('tfm_arrest_time')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_arrest_time')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Case Number:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmEventNumber"
                        value={getValue('tfm_event_number')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                        placeholder="Enter case number"
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_event_number')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Approval to Arrest Granted By ICE Officer Name:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmIceApprovalOfficerName"
                        value={getValue('tfm_ice_approval_officer_name')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_ice_approval_officer_name')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">Date/Time of ICE Approval:</span>
                    {isEditing ? (
                      <input
                        type="datetime-local"
                        name="tfmIceApprovalDatetime"
                        value={getValue('tfm_ice_approval_datetime')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_ice_approval_datetime')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Arrest Location:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmArrestLocation"
                        value={getValue('tfm_arrest_location')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_arrest_location')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Consent to Search / Who Granted Consent:</span>
                    {isEditing ? (
                      <input
                        type="text"
                        name="tfmConsentGrantedBy"
                        value={getValue('tfm_consent_granted_by')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_consent_granted_by')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Time Consent Was Granted:</span>
                    {isEditing ? (
                      <input
                        type="time"
                        name="tfmConsentTime"
                        value={getValue('tfm_consent_time')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_consent_time')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="detailLabel">Time Search Completed:</span>
                    {isEditing ? (
                      <input
                        type="time"
                        name="tfmSearchCompletedTime"
                        value={getValue('tfm_search_completed_time')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_search_completed_time')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Hours Spent on Investigation:</span>
                    {isEditing ? (
                      <input
                        type="number"
                        name="tfmInvestigationHours"
                        value={getValue('tfm_investigation_hours')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                        step="0.25"
                        min="0"
                        max="24"
                      />
                    ) : (
                      <input
                        type="text"
                        value={getValue('tfm_investigation_hours')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>

                <div className="detailRow">
                  <div className="detailItem">
                    <span className="detailLabel">Notes:</span>
                    {isEditing ? (
                      <textarea
                        name="tfmNotes"
                        value={getValue('tfm_notes')}
                        onChange={handleInputChange}
                        className="modern-input"
                        style={editableFieldStyle}
                        rows="4"
                      />
                    ) : (
                      <textarea
                        value={getValue('tfm_notes')}
                        className="modern-input"
                        style={viewOnlyFieldStyle}
                        rows="4"
                        readOnly
                        disabled
                      />
                    )}
                  </div>
                </div>
              </>
            )}

          </div>
        ) : (
          <div className="loadingMessage">Loading record details...</div>
        )}
      </div>

      <div className="modalFooter" style={{ display: 'flex', justifyContent: 'space-between' }}>
        <div style={{ display: 'flex', gap: '10px', flex: '1', justifyContent: 'center' }}>
          {isEditing ? (
            <>
              <button
                className="modern-btn"
                style={{ backgroundColor: '#388e3c' }}
                onClick={handleSaveChanges}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                className="modern-btn"
                style={{ backgroundColor: '#f44336' }}
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
            </>
          ) : (
            <>
              {/* Only show approval button for unapproved records and supervisors */}
              {!isApprovedRecord && canApproveRecords() && (
                <>
                  {showApprovalSuccess ? (
                    <button
                      className="modern-btn"
                      style={{ backgroundColor: '#388e3c' }}
                      disabled={true}
                    >
                      Record Approved ✓
                    </button>
                  ) : (
                    <button
                      className="modern-btn"
                      style={{ backgroundColor: '#388e3c' }}
                      onClick={handleApproveRecord}
                      disabled={approving}
                    >
                      {approving ? 'Approving...' : 'Approve'}
                    </button>
                  )}
                </>
              )}

              {/* Show message for submitters who cannot approve */}
              {!isApprovedRecord && !canApproveRecords() && (
                <button
                  className="modern-btn"
                  style={{ backgroundColor: '#9e9e9e', cursor: 'default' }}
                  disabled={true}
                  title="Only Supervisors can approve records"
                >
                  Approval Required (Supervisor Only)
                </button>
              )}

              {/* Show edit button for unapproved records and users with edit permissions, or for Admin/Security Admin on any record */}
              {/* Hide edit button for officers viewing from all-records tab */}
              {((!isApprovedRecord && canEditRecords()) || (isAdmin || isSecurityAdmin)) &&
               !(isOfficer && viewContext === 'all-records') && (
                <button
                  className="modern-btn"
                  style={{ backgroundColor: '#1976d2' }}
                  onClick={handleEditClick}
                >
                  Edit
                </button>
              )}

              {/* Show status for approved records */}
              {isApprovedRecord && (
                <button
                  className="modern-btn"
                  style={{ backgroundColor: '#388e3c', cursor: 'default' }}
                  disabled={true}
                >
                  ✓ Approved Record (Read Only)
                </button>
              )}
            </>
          )}
        </div>
        <div>
          {!isEditing && <button className="modern-btn" onClick={onClose}>Close</button>}
        </div>
      </div>
    </div>
  );
};

// Main component that conditionally renders the modal content
const RecordDetailsModal = ({ record, onClose, visible, onApprove, onUpdate, startInEditMode = false, viewContext = null }) => {
  if (!visible) return null;

  return (
    <div className="modalOverlay">
      <ModalContent
        record={record}
        onClose={onClose}
        onApprove={onApprove}
        onUpdate={onUpdate}
        startInEditMode={startInEditMode}
        viewContext={viewContext}
      />
    </div>
  );
};

export default RecordDetailsModal;
